import { UploadFile } from 'antd/lib/upload/interface';
import cn from 'classnames';
import {
  ClipboardText,
  CloseCircle,
  DocumentDownload,
  Eye,
} from 'iconsax-react';
import { useFormContext } from 'react-hook-form';
import InfoText from './InfoText';

type Props = {
  file: UploadFile;
  fileList: UploadFile[];
  status?: 'error' | 'success';
  disableDeleteBtn?: boolean;
  maxLengthFileName?: number;
  setFileList?: ({
    file,
    fileList,
  }: {
    file?: UploadFile;
    fileList: UploadFile[];
  }) => void;
  onDeleteDocument?: (id?: string) => void;
  dataTestId?: string;
};

function UploadFileItem({
  file,
  fileList,
  setFileList,
  status,
  disableDeleteBtn,
  maxLengthFileName = 50,
  onDeleteDocument,
  dataTestId,
}: Props) {
  const { uid } = file;
  const id = (file as any).id;
  
  // Optional chaining in case it's used outside a FormProvider
  const formContext = useFormContext();
  const documentDeleteIds = formContext?.watch ? formContext.watch('documentDeleteIds') : undefined;
  const setValue = formContext?.setValue;

  const handleRemove = (uid: string, id?: number | string) => {
    if (documentDeleteIds && setValue) {
      setValue('documentDeleteIds', [...documentDeleteIds, id?.toString()]);
    }
    const updatedFileList = fileList?.filter((f) => f.uid !== uid);
    if (setFileList) {
      setFileList({
        fileList: updatedFileList?.length === 0 ? [] : updatedFileList,
      });
    }
  };

  const truncateFilename = (filename: string, length: number): string => {
    if (filename?.length > length) {
      return filename?.slice(0, length) + '...';
    }
    return filename;
  };

  function download(url: string, filename: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  const fileExt = file?.name?.split('.').pop()?.toLowerCase();
  const inferredType = file?.type || (fileExt ? `.${fileExt}` : undefined);

  const isFileIsVideo = [
    'video/mp4',
    'video/webm',
    '.mp4',
    '.webm',
    'VIDEO',
  ].includes(inferredType as string);

  const isFileIsImage = [
    'image/jpeg',
    'image/png',
    'image/jpg',
    'image/webp',
    '.jpeg',
    '.png',
    '.jpg',
    '.webp',
    'jpeg',
    'png',
    'jpg',
    'webp',
    'IMAGE',
  ]?.includes(inferredType as string);


  return (
    <div
      data-testid={dataTestId}
      className={cn(
        'border rounded-xl my-2 transition-colors duration-200 overflow-hidden dark:border-dark-input-border',
        {
          'bg-light-surface-primary dark:bg-dark-surface-primary': true,
          'border-light-border dark:border-dark-border': !status,
          'border-red-600 dark:border-red-500': status === 'error',
          'border-green-600 dark:border-green-500': status === 'success',
        }
      )}
    >
      <div>
        <div className="">
          <div className="w-full flex items-center justify-between p-1">
            <div className="flex items-center gap-1">
              {isFileIsVideo ? (
                <video
                  controls
                  preload="auto"
                  crossOrigin="anonymous"
                  className="w-14 h-14 shrink-0 rounded-md object-cover"
                >
                  <source
                    src={(file.originFileObj ? URL.createObjectURL(file.originFileObj) : '') || file?.url || (file as any)?.blobFileUrl || file?.thumbUrl}
                  />
                </video>
              ) : isFileIsImage ? (
                <img
                  src={(file.originFileObj ? URL.createObjectURL(file.originFileObj) : '') || file?.url || (file as any)?.blobFileUrl || file?.thumbUrl}
                  className="w-14 h-14 object-cover shrink-0 rounded-md bg-gray-50 dark:bg-gray-800"
                />
              ) : (
                <div className="flex flex-col gap-1 items-center justify-center w-14 h-14 shrink-0 rounded-md bg-gray-50 dark:bg-gray-800">
                  <ClipboardText
                    size="32"
                    variant="Bold"
                    className="dark:text-dark-text-primary"
                  />
                </div>
              )}

              {/* Name and size */}
              <div className="flex flex-col">
                <InfoText
                  text={truncateFilename(file?.name || (file as any)?.fileName || (file?.url && typeof file.url === 'string' ? file.url.split('/').pop()?.replace(/^[0-9a-fA-F-]{36}_/, '') : '') || 'Unknown file', maxLengthFileName)}
                  className="!text-light-text-primary dark:!text-dark-text-primary"
                />

                {((file?.size !== undefined && file.size !== 0) || (file as any)?.blobSize !== undefined) && !isNaN(Number(file.size || (file as any).blobSize)) && (
                  <InfoText
                    text={`${(Number(file?.size || (file as any)?.blobSize) / 1024 / 1024).toFixed(2)} MB`}
                    size="sm"
                  />
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="w-8 h-8 flex items-center justify-center rounded-full p-2 bg-[#F3F6FD] dark:bg-opacity-10"
                onClick={(e) => {
                  e.stopPropagation();
                  const url =
                    file?.url ||
                    (file as any)?.blobFileUrl ||
                    (file?.originFileObj &&
                      URL.createObjectURL(file.originFileObj));

                  if (!url) return;

                  if (!isFileIsImage && !isFileIsVideo) {
                    window.open(url, '_blank');
                    return;
                  }
                  const newTab = window.open();

                  if (newTab) {
                    newTab.document.write(`
                    <html>
                      <body style="margin:0;display:flex;align-items:center;justify-content:center;background:#000;">
                      ${
                        isFileIsImage
                          ? `<img src="${url}" style="max-width:100%;max-height:100%;" />`
                          : `<video controls autoplay style="max-width:100%;max-height:100%;">
            <source src="${url}">
            Your browser does not support the video tag.
          </video>`
                      }
                      </body>
                    </html>
                    `);
                  }
                }}
              >
                <Eye size="16" className="!text-light-information" />
              </button>
              <button
                type="button"
                className="w-8 h-8 flex items-center justify-center rounded-full p-2 bg-[#F2F4F4] dark:bg-opacity-10"
                onClick={(e) => {
                  e.stopPropagation();
                  const url =
                    file?.url ||
                    (file as any)?.blobFileUrl ||
                    (file?.originFileObj &&
                      URL.createObjectURL(file.originFileObj));

                  if (url) {
                    download(url, file?.name || (file as any)?.fileName);
                  }
                }}
              >
                <DocumentDownload size="16" className="!text-light-success" />
              </button>

              {!disableDeleteBtn && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteDocument?.(id as string);
                    handleRemove(uid, id as string);
                  }}
                  aria-label="Delete file"
                  type="button"
                  data-testid={
                    dataTestId ? `${dataTestId}-delete-btn` : undefined
                  }
                  className="w-8 h-8 flex items-center justify-center rounded-full p-2 bg-[#FAF2F3] dark:bg-opacity-10"
                >
                  <CloseCircle size="16" className="!text-light-warning" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UploadFileItem;
